import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MessageSquare, Clipboard } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const StartChatDialog = ({ user, tasks, onStartChat }) => {
  const [open, setOpen] = useState(false);

  // Filter tasks assigned to this user
  const userTasks = tasks.filter(task => task.assigned_to === user.id);

  const handleSelectTask = (task) => {
    onStartChat(task);
    setOpen(false);
  };

  if (userTasks.length === 0) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="gap-1 text-slate-400"
        title="No tasks assigned to this user"
      >
        <MessageSquare className="w-4 h-4" />
        Chat
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1 text-teal-600 hover:text-teal-700 hover:bg-teal-50"
          data-testid={`start-chat-staff-btn-${user.id}`}
        >
          <MessageSquare className="w-4 h-4" />
          Chat
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Start Chat with {user.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Select a task to start chatting about:
          </p>
          
          <div className="max-h-96 overflow-y-auto space-y-3">
            {userTasks.map(task => (
              <Card
                key={task.id}
                className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => handleSelectTask(task)}
                data-testid={`chat-task-${task.id}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-slate-900">{task.title}</h4>
                  <Badge className={
                    task.status === 'completed' ? 'bg-green-100 text-green-800' :
                    task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    task.status === 'overdue' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }>
                    {task.status.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2">{task.description}</p>
                {task.customer_name && (
                  <p className="text-xs text-slate-500 mt-2">
                    Customer: {task.customer_name}
                  </p>
                )}
              </Card>
            ))}
          </div>

          {userTasks.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Clipboard className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No tasks assigned to this user</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StartChatDialog;
